import { useCallback, useMemo, useState } from 'react';
import { matchSorter } from 'match-sorter';
import type { SyntheticEvent } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import type {
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams,
} from '@mui/material/Autocomplete';

import { EMPTY_STRING } from '@e-socna/empty';
import type { MissionCategoryId } from '@e-socna/data-layer';

import type { ReferencesContext_MissionCategory } from '../../contexts/ReferencesContext';

interface CategoryChoice {
  readonly branchId: MissionCategoryId | null;
  readonly label: string;
}

interface Props {
  readonly branches: readonly ReferencesContext_MissionCategory[];
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly helperText?: string | null;
  readonly id: string;
  readonly initiallyOpen?: boolean;
  readonly label?: string | null;
  readonly leaves: readonly ReferencesContext_MissionCategory[];
  readonly name?: string | null;
  readonly onBlur?: (() => void) | null;
  readonly onBranchChange: (branchId: MissionCategoryId) => void;
  readonly onLeafChange: (leaf: string) => void;
  readonly selectedBranchId: MissionCategoryId | null;
  readonly leaf: string;
}

const SUB_CATEGORIES_GROUP = 'Sous-catégories';
const LEAVES_GROUP = 'Catégories';

const choiceLabelOf = (choice: CategoryChoice | string) =>
  typeof choice === 'string' ? choice : choice.label;

export const LeafLevelInput = ({
  branches,
  disabled = false,
  error = false,
  helperText = null,
  id,
  initiallyOpen = false,
  label = null,
  leaves,
  name = null,
  onBlur = null,
  onBranchChange,
  onLeafChange,
  selectedBranchId,
  leaf,
}: Readonly<Props>) => {
  const [open, setOpen] = useState(initiallyOpen);

  const choices = useMemo<readonly CategoryChoice[]>(
    () => [
      ...leaves.map((option) => ({ branchId: null, label: option.label })),
      ...branches.map((option) => ({
        branchId: option._id,
        label: option.label,
      })),
    ],
    [branches, leaves],
  );

  const selectedBranch = choices.find(
    (choice) => !!choice.branchId && choice.branchId === selectedBranchId,
  );

  const handleChange = useCallback(
    (_event: SyntheticEvent, choice: CategoryChoice | string | null) => {
      if (choice && typeof choice !== 'string' && choice.branchId) {
        onBranchChange(choice.branchId);
        return;
      }

      onLeafChange(choice ? choiceLabelOf(choice) : EMPTY_STRING);
    },
    [onBranchChange, onLeafChange],
  );

  // The typed text is the leaf: it is kept as typed, without waiting for a
  // choice to be confirmed. The resets following a choice are not typings.
  const handleInputChange = useCallback(
    (
      _event: SyntheticEvent,
      text: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      if (reason === 'input' || reason === 'clear') {
        onLeafChange(text);
      }
    },
    [onLeafChange],
  );

  const filterOptions = useCallback(
    (
      options: CategoryChoice[],
      { inputValue }: Readonly<{ inputValue: string }>,
    ) => matchSorter(options, inputValue, { keys: ['label'] }),
    [],
  );

  const groupBy = useCallback(
    (choice: CategoryChoice) =>
      choice.branchId ? SUB_CATEGORIES_GROUP : LEAVES_GROUP,
    [],
  );

  const isOptionEqualToValue = useCallback(
    (choice: CategoryChoice, value: CategoryChoice | string) =>
      typeof value === 'string'
        ? !choice.branchId && choice.label === value
        : choice.branchId === value.branchId && choice.label === value.label,
    [],
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const renderInput = useCallback(
    (params: Readonly<AutocompleteRenderInputParams>) => (
      <TextField
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...params}
        // A level opened by the choice of the previous one is the next to fill.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={initiallyOpen}
        disabled={disabled}
        error={error}
        helperText={helperText}
        label={label}
        name={name ?? undefined}
        onBlur={onBlur ?? undefined}
        placeholder="Choisir ou saisir"
      />
    ),
    [disabled, error, helperText, initiallyOpen, label, name, onBlur],
  );

  return (
    <Autocomplete<CategoryChoice, false, false, true>
      disabled={disabled}
      filterOptions={filterOptions}
      freeSolo
      getOptionLabel={choiceLabelOf}
      groupBy={branches.length ? groupBy : undefined}
      id={id}
      inputValue={selectedBranch?.label ?? leaf}
      isOptionEqualToValue={isOptionEqualToValue}
      onChange={handleChange}
      onClose={handleClose}
      onInputChange={handleInputChange}
      onOpen={handleOpen}
      open={open}
      options={choices}
      renderInput={renderInput}
      value={selectedBranch ?? (leaf || null)}
    />
  );
};
